import T from "@/components/forms/TextField.module.css";

import { ariaTanstackFieldProps } from "@/components/forms/ariaTanstackFieldProps.tsx";
import { CurrencySymbol } from "@/components/l10n/Currency.tsx";
import { ModalResult } from "@/components/modal/ModalResult.ts";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { BaseCurrencyField } from "@/modules/billing/BaseCurrencyField.tsx";
import { modifyLowBalanceNoticeSettingsMutation } from "@/modules/billing/billingAccountQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { ErrorTexts } from "@/modules/billing/payment_forms/components.tsx";
import { DisplayCurrencyRate } from "@/modules/currency/DisplayCurrencyRate.tsx";
import { useCurrentCurrency } from "@/modules/currency/useCurrentCurrency.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useForm, useStore } from "@tanstack/react-form";
import type { ReactNode } from "react";
import { Input, Label, NumberField } from "react-aria-components";

export interface LowBalanceNotificationsModalProps {
    billingAccount: EBillingAccount;
}

function LowBalanceNotificationsModalContent({ billingAccount }: LowBalanceNotificationsModalProps) {
    //#region Hooks
    const { t } = useLingui();
    const modifyMutation = useStandardMutation(modifyLowBalanceNoticeSettingsMutation);

    const currency = useCurrentCurrency();

    const {
        id,
        low_balance_notice_settings: { threshold, is_enabled },
    } = billingAccount.account;

    const form = useForm({
        defaultValues: {
            threshold: currency.round(currency.fromSite(threshold!)),
        },
        async onSubmit({ value: { threshold } }) {
            await modifyMutation.mutateAsync({
                body: {
                    billing_account_id: id,
                    threshold: currency.toSite(threshold),
                    is_enabled: true,
                },
            });
        },
    });
    const isSubmitting = useStore(form.store, (s) => s.isSubmitting);
    //#endregion

    return (
        <WModalContent
            title={t`Low balance notifications`}
            label={!is_enabled ? t`Enable` : t`Save`}
            modalAction={async () => {
                await form.handleSubmit();
                return form.state.isValid ? ModalResult.CLOSE : ModalResult.KEEP_OPEN;
            }}
            className="flex flex-col gap-3 max-w-32rem"
        >
            <p>
                <Trans>
                    Notify when balance goes below the following threshold. Keep in mind that the actual limit is
                    defined in base currency.
                </Trans>
            </p>

            <div className="flex gap-0.5rem w-full">
                <div className="w-50%">
                    <form.Field
                        name="threshold"
                        validators={{
                            // `NaN` when the field is empty
                            onBlur: ({ value }) =>
                                Number.isNaN(value) || Number(value) <= 0 ? t`Amount should be positive` : null,
                        }}
                    >
                        {(api) => (
                            <NumberField
                                className={T.NumberField}
                                autoFocus
                                isDisabled={isSubmitting}
                                {...ariaTanstackFieldProps(api)}
                            >
                                <Label className={T.Label}>
                                    <Trans comment="Amount below which a low balance notification will be sent">
                                        Threshold
                                    </Trans>{" "}
                                    (<CurrencySymbol />)
                                </Label>
                                <Input className={T.Input} />
                                <ErrorTexts errors={api.state.meta.errors} />
                            </NumberField>
                        )}
                    </form.Field>
                </div>

                <div className="w-50%">
                    <form.Subscribe selector={(s) => s.values.threshold}>
                        {(threshold) => <BaseCurrencyField value={threshold} currency={currency} />}
                    </form.Subscribe>
                </div>
            </div>

            <DisplayCurrencyRate />
        </WModalContent>
    );
}

export function LowBalanceNotificationsModal({
    button,
    ...props
}: LowBalanceNotificationsModalProps & { button: ReactNode }) {
    return (
        <WModal button={button}>
            <LowBalanceNotificationsModalContent {...props} />
        </WModal>
    );
}
